import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useClientOffers = (requestId = null, filters = {}) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.status,
    filters.min_price,
    filters.max_price,
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchOffers = useCallback(async (reqId = requestId, page = 1) => {
    if (!reqId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) =>
          value !== '' && value !== 'all' && value != null
        )
      );

      const params = {
        page,
        ...cleanFilters,
      };

      const response = await clientAPI.getQuotes(reqId, params);
      setOffers(response.data.results || response.data);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des offres:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des offres');
    } finally {
      setLoading(false);
    }
  }, [requestId, debouncedFilters]);

  const acceptOffer = useCallback(async (offerId) => {
    try {
      const response = await clientAPI.acceptQuote(offerId);
      // Recharger les offres après acceptation
      fetchOffers(requestId);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de l\'acceptation de l\'offre:', err);

      // Extraire le message d'erreur de différents champs possibles
      const errorData = err.response?.data || {};
      let errorMessage = errorData.detail || errorData.message || errorData.error;

      // Si c'est une ValidationError, le message peut être dans un champ spécifique
      if (!errorMessage && typeof errorData === 'object') {
        // Chercher dans les champs de validation (driver, vehicle, etc.)
        const validationFields = ['driver', 'vehicle', 'non_field_errors'];
        for (const field of validationFields) {
          if (errorData[field]) {
            const fieldError = Array.isArray(errorData[field])
              ? errorData[field][0]
              : errorData[field];
            if (fieldError) {
              errorMessage = fieldError;
              break;
            }
          }
        }
      }

      return {
        success: false,
        error: errorMessage || 'Erreur lors de l\'acceptation de l\'offre'
      };
    }
  }, [requestId, fetchOffers]);

  const rejectOffer = useCallback(async (offerId) => {
    try {
      const response = await clientAPI.rejectQuote(offerId);
      // Recharger les offres après rejet
      fetchOffers(requestId);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors du rejet de l\'offre:', err);
      return {
        success: false,
        error: err.response?.data?.detail || 'Erreur lors du rejet de l\'offre'
      };
    }
  }, [requestId, fetchOffers]);

  const getOfferById = useCallback(async (offerId) => {
    try {
      const response = await clientAPI.getQuoteById(offerId);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors du chargement de l\'offre:', err);
      return {
        success: false,
        error: err.response?.data?.detail || 'Erreur lors du chargement de l\'offre'
      };
    }
  }, []);

  useEffect(() => {
    if (requestId) {
      fetchOffers(requestId);
    }
  }, [requestId, fetchOffers]);

  return {
    offers,
    loading,
    error,
    pagination,
    fetchOffers,
    acceptOffer,
    rejectOffer,
    getOfferById,
  };
};
